// The Storefront (Sell Playlists) state behind the Profile page: the setlist
// price + its number pad, the ON/OFF sale switch, the Storefront v2 flag, the
// sample-page preview, and the composer deep link. Split out of
// pages/ProfilePage.tsx; StorefrontSheets renders the drawers.
import { takesMoney } from "../utils/takesMoney";
import { openMoneyGate } from "../lib/connectBank";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usersApi } from "../services/users";
import { PayinCurrency, maxPriceFor, minPriceFor, validateSetlistPrice } from "../utils/payinConfig";
import type { User } from "../types/User";
import type { ProfileAutosave } from "./useProfileAutosave";

type Args = {
  user: User | null;
  profile: User | null;
  currency: PayinCurrency;
  setOpenSheet: React.Dispatch<React.SetStateAction<null | "edit" | "settings" | "merch" | "setlists" | "feeTier" | "refer" | "leaderboard" | "discover">>;
  autosave: ProfileAutosave;
};

export function useStorefront({ user, profile, currency, setOpenSheet, autosave }: Args) {
  const { putProfileField, setSaving, markSaved, setSaveError, NETWORK_ERR } = autosave;
  // Deep link from the composer's "copy this link": a DJ on their
  // laptop opens /requests?sheet=setlists&add=1#profile and lands with the
  // Sell Playlists sheet AND the upload composer already open — the same
  // pattern as the accept email's /balance?upload=1. Params are consumed
  // once, then stripped so refresh/back don't re-fire.
  const [deepLinkParams] = useSearchParams();
  const [shopAutoAdd, setShopAutoAdd] = useState(false);
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current) return;
    if (deepLinkParams.get("sheet") !== "setlists") return;
    deepLinkedRef.current = true;
    setOpenSheet("setlists");
    if (deepLinkParams.get("add") === "1") setShopAutoAdd(true);
    // Strip the consumed params with a plain history replace — the router's
    // setSearchParams can drop location.hash, and losing #profile here would
    // close the drawer this link just opened.
    const next = new URLSearchParams(window.location.search);
    next.delete("sheet");
    next.delete("add");
    const qs = next.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    );
  }, [deepLinkParams, setOpenSheet]);

  // Storefront — Sell Playlists (Setlist sales, live). Price via the three
  // suggested chips (scaled off the request price) or the Custom NumberPad;
  // the ON/OFF toggle saves setlistSaleEnabled (server rejects enabling
  // without a floor-clearing price — that 400 shows inline).
  const [setlistPriceInput, setSetlistPriceInput] = useState("");
  const [setlistPadOpen, setSetlistPadOpen] = useState(false);
  const [setlistDraft, setSetlistDraft] = useState("");
  // Custom-price fix (founder decision): the NumberPad is a fixed bottom sheet
  // that covered the price card — scroll the card up when the pad opens so
  // the DJ types while watching the amount.
  const setlistPriceCardRef = useRef<HTMLDivElement | null>(null);
  const [setlistEnabled, setSetlistEnabled] = useState(false);
  // Storefront v2 capability (server's STOREFRONT_V2 env flag). Absent on an
  // older server or with the flag off ⇒ false ⇒ today's single-playlist
  // sheet, byte-for-byte. NEVER infer v2 from the presence of shopPlaylists.
  const [storefrontV2, setStorefrontV2] = useState(false);
  const [showSetlistPreview, setShowSetlistPreview] = useState(false);
  // How many playlists are on the shelf, for the On/Off on the Profile row: what
  // the server said at load, until the shelf reports its own count.
  const [shelfCount, setShelfCount] = useState<number | null>(null);
  const playlistsOnSale = shelfCount ?? profile?.shopPlaylists?.length ?? 0;

  // Chip tap or Custom-pad commit — one save path. Amounts are clamped into
  // [floor, max] and validated with the exact server mirror before the PUT
  // (the server re-validates authoritatively).
  // FLOOR FIX (founder: "I'm not able to enter anything below
  // $4.99!? can be any price even free"). This clamp was the culprit: it
  // silently raised anything under setlistFloor back up to it, so typing $3
  // saved $4.99 and typing 0 saved $4.99. setlistFloor was a PRODUCT rule
  // from earlier, never a technical limit — the real bound is the same
  // per-currency minPrice song requests already use, and 0 is now a price.
  const saveSetlistPrice = (raw: number) => {
    const isFree = Number(raw) === 0;
    const clamped = isFree
      ? 0
      : Math.max(minPriceFor(currency), Math.min(raw, maxPriceFor(currency, profile?.capsLifted)));
    const check = validateSetlistPrice(clamped, currency, profile?.capsLifted);
    if (!check.ok || check.price == null) return;
    setSetlistPriceInput(String(check.price));
    putProfileField("setlistPrice", { setlistPrice: String(check.price) });
  };

  const commitSetlistPad = () => {
    const t = setlistDraft.trim();
    if (t !== "") {
      const n = Number(t.replace(",", "."));
      // >= 0, not > 0: typing a bare 0 on the pad is how a DJ prices a
      // setlist free.
      if (Number.isFinite(n) && n >= 0) saveSetlistPrice(n);
    }
    setSetlistDraft("");
    setSetlistPadOpen(false);
  };

  // ON/OFF for selling. The server rejects enabling without a saved
  // floor-clearing price — surface that exact message inline and revert.
  const toggleSetlistSale = async () => {
    if (!user) return;
    // Free tier: selling is money. The switch stays where it is and opens
    // the money gate until the talent can take money.
    if (!takesMoney(profile)) return openMoneyGate(profile);
    const next = !setlistEnabled;
    setSetlistEnabled(next);
    setSaving("setlistSale");
    try {
      const fd = new FormData();
      fd.append("setlistSaleEnabled", String(next));
      await usersApi.update(user.slug, fd);
      markSaved("setlistSale");
      window.dispatchEvent(
        new CustomEvent("profile:patched", { detail: { setlistSaleEnabled: String(next) } })
      );
    } catch (e: any) {
      setSetlistEnabled(!next);
      setSaveError("setlistSale", e?.response?.data?.msg || NETWORK_ERR);
    }
  };

  return {
    shopAutoAdd,
    setShopAutoAdd,
    setlistPriceInput,
    setSetlistPriceInput,
    setlistPadOpen,
    setSetlistPadOpen,
    setlistDraft,
    setSetlistDraft,
    setlistPriceCardRef,
    setlistEnabled,
    setSetlistEnabled,
    storefrontV2,
    setStorefrontV2,
    showSetlistPreview,
    setShowSetlistPreview,
    playlistsOnSale,
    setShelfCount,
    saveSetlistPrice,
    commitSetlistPad,
    toggleSetlistSale,
  };
}

export type Storefront = ReturnType<typeof useStorefront>;
